// frontend/src/pages/NewUIRecharge.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';

// Botón gaming reutilizable
function GamingButton({ variant = "secondary", className = "", leftIcon, children, disabled, ...props }) {
    const base =
        "inline-flex items-center justify-center gap-2.5 " +
        "h-12 md:h-14 px-6 md:px-8 " +
        "rounded-[14px] md:rounded-[16px] " +
        "font-bold tracking-wider leading-none " +
        "transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98] " +
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100";

    const variants = {
        primary: "bg-accent text-secondary hover:bg-accent-light border-2 border-accent-dark",
        secondary: "bg-primary text-white hover:bg-primary-light border-2 border-primary-dark",
        ctaBlue: "bg-[#2284DF] text-white hover:bg-[#2A91F4] border-2 border-[#1E75C6]",
        ctaYellow: "bg-[#FCE503] text-[#122436] hover:bg-[#FCE503]/90 border-2 border-[#E5CF03]",
    };

    return (
        <button
            className={`${base} ${variants[variant]} ${className}`}
            disabled={disabled}
            {...props}
        >
            {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
            <span className="whitespace-nowrap">{children}</span>
        </button>
    );
}

// Componente de opción de tickets
const TicketOption = ({ amount, selected, onSelect }) => (
    <button
        type="button"
        onClick={() => onSelect(amount)}
        className={`relative cursor-pointer p-6 rounded-[18px] transition-all transform hover:scale-105 
                   shadow-[0_8px_24px_rgba(0,0,0,0.35)] flex flex-col items-center gap-3
                   ${selected
            ? 'bg-gradient-to-br from-[#FCE503] to-[#E5CF03] border-3 border-[#122436]'
            : 'bg-[#122436] border-2 border-white/30 hover:border-[#2284DF]'
        }`}
    >
        <span className={`text-4xl sm:text-5xl font-extrabold ${selected ? 'text-[#122436]' : 'text-white'}`}>
            {amount}
        </span>
        <span className={`text-base sm:text-lg font-bold uppercase tracking-wide ${selected ? 'text-[#122436]' : 'text-white/80'}`}>
            Tickets
        </span>
        {selected && (
            <div
                className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                    backgroundColor: '#122436',
                    border: '2px solid #FCE503'
                }}
            >
                <svg className="w-5 h-5 text-[#FCE503]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
            </div>
        )}
    </button>
);

// Componente de info de tarjetas de test
const TestCardInfo = ({ cards }) => (
    <div
        className="rounded-[18px] p-5 mb-6"
        style={{
            backgroundColor: 'rgba(34, 132, 223, 0.15)',
            border: '2px solid #2284DF'
        }}
    >
        <div className="flex items-center mb-3">
            <svg className="w-5 h-5 text-[#2284DF] mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <h3 className="text-white font-bold uppercase tracking-wide">Modo de Pruebas Activado</h3>
        </div>
        <p className="text-white/80 mb-4 text-sm">
            El sistema está en modo de pruebas. Asegurate de no tener una sesión iniciada en Mercado Pago.
            Utilizá estas tarjetas para realizar pagos de prueba:
        </p>
        <div className="grid gap-3 md:grid-cols-2">
            {cards.map((card, index) => (
                <div
                    key={index}
                    className="rounded-lg p-3 text-sm"
                    style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)'
                    }}
                >
                    <div className="font-bold text-white mb-2">{card.type} ({card.status})</div>
                    <div className="text-white/70 space-y-1">
                        <div>Número: <span className="text-white font-semibold">{card.number}</span></div>
                        <div>CVV: <span className="text-white font-semibold">{card.cvv}</span></div>
                        <div>Exp: <span className="text-white font-semibold">{card.expirationDate}</span></div>
                        <div>Titular: <span className="text-white font-semibold">{card.holderName}</span></div>
                        <div>DNI: <span className="text-white font-semibold">{card.dni}</span></div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const NewUIRecharge = () => {
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [sandboxMode, setSandboxMode] = useState(false);
    const [testCards, setTestCards] = useState([]);
    const { updateUser } = useAuth();
    const navigate = useNavigate();

    const ticketOptions = [100, 250, 500, 1000];

    // Aplicar scope de Acumin
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        return () => {
            document.body.classList.remove('acumin-scope');
        };
    }, []);

    // Verificar si estamos en modo sandbox
    useEffect(() => {
        const checkSandboxMode = async () => {
            try {
                const response = await axios.get('/payments/test-cards');
                if (response.data.success) {
                    setSandboxMode(true);
                    setTestCards(response.data.testCards);
                }
            } catch (error) {
                console.error('Error al verificar modo sandbox:', error);
            }
        };

        checkSandboxMode();
    }, []);

    const handlePurchase = async () => {
        if (!selectedAmount) {
            setError('Por favor seleccioná una cantidad de tickets');
            return;
        }

        setIsProcessing(true);
        setError('');

        try {
            // Crear preferencia de pago con MercadoPago
            const response = await axios.post('/payments/create-preference', {
                amount: selectedAmount
            });

            if (response.data.success) {
                // Redirigir al usuario a la página de pago de MercadoPago
                window.location.href = response.data.paymentUrl;
            } else {
                setError(response.data.message || 'Error al procesar la compra');
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Error en la compra:', error);
            setError('Error al procesar la compra. Por favor intentá de nuevo.');
            setIsProcessing(false);
        }
    };

    return (
        <div
            className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8"
            style={{
                backgroundImage: 'url(/images/backgrounds/hero-radial-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1
                        className="toon toon-3d text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase mb-3"
                        style={{
                            WebkitTextStroke: "1px #000",
                            textShadow: "0 2px 0 rgba(0,0,0,1), 0 4px 2px rgba(0,0,0,1)"
                        }}
                    >
                        Recarga tus Tickets
                    </h1>
                    <p className="text-white/80 text-base sm:text-lg">
                        Seleccioná la cantidad de tickets que deseas comprar
                    </p>
                </div>

                {/* Main Card */}
                <div
                    className="rounded-[24px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                    style={{
                        backgroundColor: '#122436',
                        border: '2px solid rgba(255,255,255,0.55)'
                    }}
                >
                    <div className="p-6 sm:p-10">
                        {/* Sandbox Mode Info */}
                        {sandboxMode && <TestCardInfo cards={testCards} />}

                        {/* Error Message */}
                        {error && (
                            <div
                                className="mb-6 rounded-lg p-4 text-sm flex items-start gap-3"
                                style={{
                                    backgroundColor: 'rgba(253, 2, 3, 0.15)',
                                    border: '2px solid #FD0203'
                                }}
                            >
                                <svg className="w-5 h-5 text-[#FD0203] shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span className="text-[#FFB3B3]">{error}</span>
                            </div>
                        )}

                        {/* Grid de opciones */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-8">
                            {ticketOptions.map((amount) => (
                                <TicketOption
                                    key={amount}
                                    amount={amount}
                                    selected={selectedAmount === amount}
                                    onSelect={setSelectedAmount}
                                />
                            ))}
                        </div>

                        {/* Precio calculado */}
                        {selectedAmount && (
                            <div
                                className="text-center mb-8 py-5 rounded-[18px]"
                                style={{
                                    backgroundColor: 'rgba(252, 229, 3, 0.1)',
                                    border: '2px solid rgba(252, 229, 3, 0.3)'
                                }}
                            >
                                <p className="text-white/80 text-sm mb-2 uppercase tracking-wide">
                                    Total a pagar
                                </p>
                                <p className="text-4xl font-extrabold text-[#FCE503] mb-1">
                                    ${selectedAmount * 10} ARS
                                </p>
                                <p className="text-sm text-white/60">
                                    (Precio por ticket: $10 ARS)
                                </p>
                            </div>
                        )}

                        {/* Sección de pago */}
                        <div className="mt-8">
                            {/* Security badges */}
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6 pb-6 border-b border-white/10">
                                <div className="flex items-center gap-2 text-white/80">
                                    <svg className="w-5 h-5 text-[#FCE503]" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm font-semibold uppercase tracking-wide">Pago seguro</span>
                                </div>
                                <div className="flex items-center gap-2 text-white/80">
                                    <svg className="w-5 h-5 text-[#2284DF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    <span className="text-sm font-semibold uppercase tracking-wide">Procesado por MercadoPago</span>
                                </div>
                            </div>

                            {/* Payment Button */}
                            <GamingButton
                                onClick={handlePurchase}
                                disabled={!selectedAmount || isProcessing}
                                variant="ctaYellow"
                                className="w-full text-base sm:text-lg uppercase"
                                leftIcon={
                                    isProcessing ? (
                                        <div className="w-5 h-5 border-2 border-[#122436] border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                    )
                                }
                            >
                                {isProcessing ? 'Procesando...' : 'Pagar con MercadoPago'}
                            </GamingButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewUIRecharge;